import {
  ApiBlueprint,
  createApiRef,
  createExtensionInput,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import type { AppTypeModule } from './appTypeModule';
import { appTypeModuleDataRef } from './AppTypeModuleBlueprint';

/** Le registre des modules de type d'application. */
export interface AppTypeModulesApi {
  /** Tous les modules, dans l'ordre de l'application ; fixe après le démarrage. */
  list(): readonly AppTypeModule[];
  /** Le module du type `appType`, s'il y en a un. */
  get(appType: string | undefined): AppTypeModule | undefined;
}

export const appTypeModulesApiRef = createApiRef<AppTypeModulesApi>().with({
  id: 'waadoo.app-type-modules',
});

/**
 * Le registre sur une liste de modules. Deux modules du même type : le
 * premier l'emporte, comme `get` le trouverait ; `list` les garde tous,
 * leurs hooks étant appelés quoi qu'il en soit (`useAppTypeModules.ts`).
 */
export function createAppTypeModulesApi(
  modules: readonly AppTypeModule[],
): AppTypeModulesApi {
  const frozen = Object.freeze([...modules]);
  return {
    list: () => frozen,
    get: appType =>
      appType === undefined
        ? undefined
        : frozen.find(module => module.appType === appType),
  };
}

/**
 * `api:app/app-type-modules` : les modules attachés à son entrée `modules`
 * (AppTypeModuleBlueprint). Sur le plugin `app`, comme le registre
 * d'icônes : une API de l'application, que tout plugin lit.
 */
const appTypeModulesApi = ApiBlueprint.makeWithOverrides({
  name: 'app-type-modules',
  inputs: {
    modules: createExtensionInput([appTypeModuleDataRef]),
  },
  factory: (originalFactory, { inputs }) =>
    originalFactory(defineParams =>
      defineParams({
        api: appTypeModulesApiRef,
        deps: {},
        factory: () =>
          createAppTypeModulesApi(
            inputs.modules.map(module => module.get(appTypeModuleDataRef)),
          ),
      }),
    ),
});

/**
 * Le registre, enregistré dans packages/app/src/App.tsx. Sans lui, aucun
 * module n'est lu : les vues du cœur s'affichent sans leurs apports
 * (`useAppTypeModules`).
 */
export const appTypeModulesModule = createFrontendModule({
  pluginId: 'app',
  extensions: [appTypeModulesApi],
});
